// src/pages/FacturesImpayees.jsx
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Badge, Modal, Form, InputGroup, Pagination, Tooltip, OverlayTrigger } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

export default function FacturesImpayees() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedFacture, setSelectedFacture] = useState(null);
    const [showPaiementModal, setShowPaiementModal] = useState(false);
    const [showHistoriqueModal, setShowHistoriqueModal] = useState(false);
    const [showRelanceModal, setShowRelanceModal] = useState(false);
    const [sendingRelance, setSendingRelance] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [paiementData, setPaiementData] = useState({
        montant: '',
        mode_reglement: 'especes',
        reference: '',
        date_reglement: new Date().toISOString().split('T')[0]
    });

    const [relanceData, setRelanceData] = useState({
        type: 'standard',
        message_personnalise: ''
    });

    const [notification, setNotification] = useState({ show: false, type: '', title: '', message: '' });

    const showNotification = (type, title, message) => {
        setNotification({ show: true, type, title, message });
        setTimeout(() => setNotification({ show: false, type: '', title: '', message: '' }), 4000);
    };

    /* ============================================================
       FETCH
       ============================================================ */
    useEffect(() => {
        fetchFacturesImpayees();
        let interval;
        if (autoRefresh) interval = setInterval(fetchFacturesImpayees, 60000);
        return () => clearInterval(interval);
    }, [autoRefresh]);

    const fetchFacturesImpayees = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/factures_impayees.php`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            const result = await response.json();
            setData(result);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleViewHistorique = async (facture) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/factures_impayees.php?action=paiements&id=${facture.id_facture}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            setSelectedFacture({ ...facture, paiements: result.paiements });
            setShowHistoriqueModal(true);
        } catch (err) {
            showNotification('error', 'Erreur', "Impossible de charger l'historique");
        }
    };

    const handleSaisirPaiement = (facture) => {
        setSelectedFacture(facture);
        setPaiementData({
            ...paiementData,
            montant: facture.reste_a_payer,
            date_reglement: new Date().toISOString().split('T')[0]
        });
        setShowPaiementModal(true);
    };

    const handlePaiementSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            if (!user || !user.id) {
                showNotification('error', 'Erreur', "Utilisateur non identifié");
                return;
            }
            const response = await fetch(`${API_BASE_URL}/factures.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'encaisser',
                    id_facture: selectedFacture.id_facture,
                    montant: parseFloat(paiementData.montant),
                    mode_reglement: paiementData.mode_reglement,
                    reference: paiementData.reference,
                    date_reglement: paiementData.date_reglement,
                    id_utilisateur: user.id
                })
            });
            if (response.ok) {
                setShowPaiementModal(false);
                fetchFacturesImpayees();
                showNotification('success', 'Succès', "Paiement enregistré avec succès");
                setPaiementData({ montant: '', mode_reglement: 'especes', reference: '', date_reglement: new Date().toISOString().split('T')[0] });
            } else {
                const error = await response.json();
                showNotification('error', 'Erreur', error.message || "Échec de l'enregistrement");
            }
        } catch (err) {
            showNotification('error', 'Erreur', "Erreur de connexion au serveur");
        }
    };

    const handleEnvoyerRelance = (facture) => {
        setSelectedFacture(facture);
        setShowRelanceModal(true);
    };

    const handleConfirmerRelance = async () => {
        if (!selectedFacture) return;
        setSendingRelance(true);
        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            const response = await fetch(`${API_BASE_URL}/relances.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_facture: selectedFacture.id_facture,
                    id_utilisateur: user?.id,
                    type_relance: relanceData.type,
                    message: relanceData.message_personnalise
                })
            });
            const result = await response.json();
            if (result.success) {
                setShowRelanceModal(false);
                showNotification('success', 'Succès', "Relance envoyée avec succès");
                setRelanceData({ type: 'standard', message_personnalise: '' });
            } else {
                showNotification('error', 'Erreur', result.message || "Échec de l'envoi");
            }
        } catch (err) {
            showNotification('error', 'Erreur', "Erreur de connexion au serveur");
        } finally {
            setSendingRelance(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getPrioriteConfig = (priorite, jours) => {
        const configs = {
            'expiree': { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', text: `Expirée (${jours}j)`, icon: 'ri-alert-line' },
            'urgente': { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: `Urgente (J-${jours})`, icon: 'ri-alarm-warning-line' },
            'normale': { gradient: 'linear-gradient(135deg, #10b981, #059669)', text: `Dans ${jours}j`, icon: 'ri-check-line' }
        };
        return configs[priorite] || configs.normale;
    };

    const getPrioriteBadge = (priorite, jours) => {
        const config = getPrioriteConfig(priorite, jours);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.text}
            </span>
        );
    };

    const getModePaiementIcon = (mode) => {
        const icons = {
            'especes': 'ri-money-cny-circle-line',
            'cheque': 'ri-bank-line',
            'virement': 'ri-bank-card-line',
            'carte_bancaire': 'ri-bank-card-2-line',
            'traite': 'ri-file-copy-line'
        };
        return icons[mode] || 'ri-bank-line';
    };

    const getModePaiementLabel = (mode) => {
        const labels = {
            'especes': 'Espèces',
            'cheque': 'Chèque',
            'virement': 'Virement',
            'carte_bancaire': 'Carte bancaire',
            'traite': 'Traite'
        };
        return labels[mode] || mode;
    };

    const filteredFactures = data?.factures?.filter(facture => {
        const matchesSearch = facture.numero_facture.toLowerCase().includes(searchTerm.toLowerCase()) ||
            facture.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
            facture.code_client.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filter === 'all' || facture.etat_paiement === filter;
        return matchesSearch && matchesFilter;
    }) || [];

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredFactures.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredFactures.length / itemsPerPage);

    const ActionButton = ({ icon, onClick, tooltip, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    /* ============================================================
       GRAPHIQUE
       ============================================================ */
    const seriesEvolution = [{
        name: 'Montant impayé',
        data: data?.evolution?.map(item => item.montant_impaye) || []
    }];

    const optionsEvolution = {
        chart: { type: 'area', height: 300, toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
        colors: [COLORS.danger],
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.1 } },
        stroke: { curve: 'smooth', width: 2 },
        grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
        xaxis: {
            categories: data?.evolution?.map(item => {
                const [year, month] = item.periode.split('-');
                return `${month}/${year}`;
            }) || [],
            labels: { style: { fontSize: '11px' } }
        },
        yaxis: { labels: { formatter: (value) => formatCurrency(value), style: { fontSize: '11px' } } },
        tooltip: { y: { formatter: (value) => formatCurrency(value) } }
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading && !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-error-warning-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des factures impayées
                        </h5>
                        <p className="text-muted small">Analyse des créances en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={fetchFacturesImpayees} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                {/* ===== NOTIFICATION ===== */}
                {notification.show && (
                    <div className="notification-charte" style={{
                        position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
                        minWidth: '350px', background: 'white', borderRadius: '16px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                        display: 'flex', alignItems: 'center', gap: '15px',
                        transform: 'translateX(0)', opacity: 1,
                        transition: 'transform 0.3s ease',
                        borderLeft: `4px solid ${notification.type === 'success' ? COLORS.success : COLORS.danger}`,
                        overflow: 'hidden',
                        animation: 'slideInRight 0.4s ease-out'
                    }}>
                        <div style={{
                            width: '45px', height: '45px', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontSize: '24px', flexShrink: 0,
                            background: notification.type === 'success'
                                ? `linear-gradient(135deg, ${COLORS.success}, #059669)`
                                : `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`
                        }}>
                            <i className={notification.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                        </div>
                        <div style={{ flex: 1 }}>
                            <h6 style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                                {notification.title}
                            </h6>
                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>{notification.message}</p>
                        </div>
                        <button
                            onClick={() => setNotification({ show: false, type: '', title: '', message: '' })}
                            style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '5px' }}
                        >
                            <i className="ri-close-line" style={{ fontSize: '18px' }}></i>
                        </button>
                    </div>
                )}

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                                <i className="ri-error-warning-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Gestion des impayés</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-alert-line"></i>
                                        {data?.statistiques?.nb_factures_impayees || 0} factures
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-timer-line"></i>
                                        Retard max: {data?.statistiques?.retard_max_jours || 0}j
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-group-line"></i>
                                        {data?.statistiques?.nb_clients_concernes || 0} clients
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                            <div className="form-check form-switch d-flex align-items-center me-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                    id="autoRefresh"
                                />
                                <label className="form-check-label small ms-2" htmlFor="autoRefresh" style={{ color: '#64748b' }}>
                                    Auto
                                </label>
                            </div>
                            <Button className="btn-modern-primary" onClick={fetchFacturesImpayees} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-danger">
                                <div className="kpi-icon-charte"><i className="ri-error-warning-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total impayés</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.danger }}>
                                        {formatCurrency(data?.statistiques?.total_impaye || 0)}
                                    </h3>
                                    <span className="kpi-unit-charte">{data?.statistiques?.nb_factures_impayees || 0} factures</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-timer-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Montant expiré</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.warning }}>
                                        {formatCurrency(data?.statistiques?.total_expire || 0)}
                                    </h3>
                                    <span className="kpi-unit-charte">Retard max: {data?.statistiques?.retard_max_jours || 0}j</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-group-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Clients concernés</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.info }}>
                                        {data?.statistiques?.nb_clients_concernes || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">Moy: {formatCurrency(data?.statistiques?.montant_moyen || 0)}</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-calendar-check-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">À encaisser (30j)</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.success }}>
                                        {formatCurrency(data?.previsions?.trente_jours || 0)}
                                    </h3>
                                    <span className="kpi-unit-charte">{data?.previsions?.nb_factures || 0} factures</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== GRAPHIQUES ===== */}
                    <Row className="g-3 mb-4">
                        <Col xl="8">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-bar-chart-line text-white"></i>
                                        <h6 className="mb-0 fw-semibold text-white">Évolution des impayés (12 mois)</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <ReactApexChart series={seriesEvolution} options={optionsEvolution} type="area" height={300} />
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl="4">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-group-line text-white"></i>
                                        <h6 className="mb-0 fw-semibold text-white">Top clients débiteurs</h6>
                                        <span className="badge-header-light ms-auto">
                                            <i className="ri-medal-line"></i> {data?.clients?.length || 0}
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div className="table-responsive">
                                        <Table className="align-middle mb-0 table-charte">
                                            <tbody>
                                                {data?.clients?.slice(0, 5).map((client, idx) => (
                                                    <tr key={client.id_client}>
                                                        <td style={{ width: '32px' }}>
                                                            <span className="rank-badge-charte">{idx + 1}</span>
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold" style={{ fontSize: '0.82rem', color: '#1e293b' }}>
                                                                {client.raison_sociale}
                                                            </div>
                                                            <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                                                <i className="ri-barcode-line me-1"></i>
                                                                {client.code_client}
                                                            </small>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="fw-bold" style={{ color: COLORS.danger, fontSize: '0.85rem' }}>
                                                                {formatCurrency(client.total_du)}
                                                            </span>
                                                            <br />
                                                            <span className="badge-count-charte">
                                                                <i className="ri-file-list-line me-1"></i>
                                                                {client.nb_factures_impayees} facture{client.nb_factures_impayees > 1 ? 's' : ''}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>

                                    <hr style={{ margin: '0.75rem 0', borderTop: '1px solid rgba(0,0,0,0.05)' }} />

                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="small text-muted" style={{ fontSize: '0.7rem' }}>
                                            <i className="ri-wallet-line me-1"></i>
                                            Paiements partiels
                                        </span>
                                        <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                                            <i className="ri-time-line"></i>
                                            {data?.statistiques?.nb_paiements_partiels || 0}
                                        </span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="6">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher facture ou client..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="4">
                                    <Form.Select
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="all">Tous les états</option>
                                        <option value="impayee">🔴 Impayées</option>
                                        <option value="partielle">🟡 Paiements partiels</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-stack-line"></i>
                                        {filteredFactures.length} facture(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Facture</th>
                                            <th>Date</th>
                                            <th>Client</th>
                                            <th>Échéance</th>
                                            <th className="text-end">Montant TTC</th>
                                            <th className="text-end">Réglé</th>
                                            <th className="text-end">Reste</th>
                                            <th className="text-center">Retard</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map((facture) => (
                                            <tr key={facture.id_facture} className={facture.priorite === 'expiree' ? 'row-late-charte' : ''}>
                                                <td className="ps-4">
                                                    <Link to={`/ventes/factures/${facture.id_facture}`} className="table-link-charte">
                                                        {facture.numero_facture}
                                                    </Link>
                                                </td>
                                                <td className="text-muted">{formatDate(facture.date_facture)}</td>
                                                <td>
                                                    <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{facture.client}</div>
                                                    <small className="text-muted">{facture.code_client}</small>
                                                </td>
                                                <td>
                                                    <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                        {formatDate(facture.date_echeance)}
                                                    </div>
                                                    <small className={facture.jours_avant_echeance > 0 ? 'text-muted' : 'text-danger'}>
                                                        {facture.jours_avant_echeance > 0
                                                            ? `J-${facture.jours_avant_echeance}`
                                                            : `J+${Math.abs(facture.jours_retard)}`}
                                                    </small>
                                                </td>
                                                <td className="text-end fw-semibold">{formatCurrency(facture.montant_ttc)}</td>
                                                <td className="text-end" style={{ color: COLORS.success }}>
                                                    {formatCurrency(facture.montant_regle)}
                                                </td>
                                                <td className="text-end fw-bold" style={{ color: COLORS.danger }}>
                                                    {formatCurrency(facture.reste_a_payer)}
                                                </td>
                                                <td className="text-center">
                                                    {getPrioriteBadge(facture.priorite, facture.jours_retard || Math.abs(facture.jours_avant_echeance))}
                                                </td>
                                                <td className="text-center pe-4">
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <ActionButton
                                                            icon="ri-history-line"
                                                            onClick={() => handleViewHistorique(facture)}
                                                            tooltip="Historique"
                                                        />
                                                        <ActionButton
                                                            icon="ri-bank-card-line"
                                                            onClick={() => handleSaisirPaiement(facture)}
                                                            tooltip="Saisir un paiement"
                                                            color={COLORS.success}
                                                        />
                                                        <ActionButton
                                                            icon="ri-mail-send-line"
                                                            onClick={() => handleEnvoyerRelance(facture)}
                                                            tooltip="Envoyer une relance"
                                                            color={COLORS.warning}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="9">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}15, ${COLORS.success}05)`, color: COLORS.success }}>
                                                            <i className="ri-checkbox-circle-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucune facture impayée</h6>
                                                        <p className="text-muted small mb-0">Toutes les factures sont soldées</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="6" className="text-end fw-bold ps-4">TOTAL :</td>
                                            <td className="text-end fw-bold" style={{ color: COLORS.danger }}>
                                                {formatCurrency(filteredFactures.reduce((acc, f) => acc + f.reste_a_payer, 0))}
                                            </td>
                                            <td colSpan="2"></td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong> — {filteredFactures.length} facture(s)
                            </div>
                            <Pagination className="pagination-charte mb-0">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <Pagination.Item
                                        key={page}
                                        active={page === currentPage}
                                        onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                    >
                                        {page}
                                    </Pagination.Item>
                                ))}
                            </Pagination>
                        </div>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== MODAL PAIEMENT ===== */}
            <Modal show={showPaiementModal} onHide={() => setShowPaiementModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}, #059669)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-bank-card-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Enregistrer un paiement</Modal.Title>
                            <p className="text-white-50 mb-0 small">Saisissez les informations du règlement</p>
                        </div>
                    </div>
                </Modal.Header>
                <Form onSubmit={handlePaiementSubmit}>
                    <Modal.Body className="p-4">
                        {selectedFacture && (
                            <>
                                <div className="summary-card-charte mb-4">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>{selectedFacture.numero_facture}</strong>
                                            <div className="small text-muted">{selectedFacture.client}</div>
                                        </div>
                                        <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                                            <i className="ri-money-euro-circle-line"></i>
                                            {formatCurrency(selectedFacture.reste_a_payer)}
                                        </span>
                                    </div>
                                </div>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="form-label-modern">Date de paiement</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="date"
                                                    value={paiementData.date_reglement}
                                                    onChange={(e) => setPaiementData({...paiementData, date_reglement: e.target.value})}
                                                    required
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="form-label-modern">Montant</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-money-euro-circle-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0.01"
                                                    max={selectedFacture.reste_a_payer}
                                                    value={paiementData.montant}
                                                    onChange={(e) => setPaiementData({...paiementData, montant: parseFloat(e.target.value)})}
                                                    required
                                                />
                                                <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-label-modern">Mode de règlement</Form.Label>
                                    <InputGroup className="input-modern">
                                        <InputGroup.Text><i className="ri-bank-line"></i></InputGroup.Text>
                                        <Form.Select
                                            value={paiementData.mode_reglement}
                                            onChange={(e) => setPaiementData({...paiementData, mode_reglement: e.target.value})}
                                            required
                                        >
                                            <option value="especes">Espèces</option>
                                            <option value="cheque">Chèque</option>
                                            <option value="virement">Virement</option>
                                            <option value="carte_bancaire">Carte bancaire</option>
                                            <option value="traite">Traite</option>
                                        </Form.Select>
                                    </InputGroup>
                                </Form.Group>
                                <Form.Group className="mb-0">
                                    <Form.Label className="form-label-modern">Référence (optionnel)</Form.Label>
                                    <InputGroup className="input-modern">
                                        <InputGroup.Text><i className="ri-file-text-line"></i></InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="N° chèque, référence virement..."
                                            value={paiementData.reference}
                                            onChange={(e) => setPaiementData({...paiementData, reference: e.target.value})}
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button onClick={() => setShowPaiementModal(false)} className="btn-modern-outline">Annuler</Button>
                        <Button type="submit" className="btn-modern-success">
                            <i className="ri-check-line me-2"></i>Enregistrer
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ===== MODAL HISTORIQUE ===== */}
            <Modal show={showHistoriqueModal} onHide={() => setShowHistoriqueModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.info}, #0dcaf0)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-history-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Historique des paiements</Modal.Title>
                            <p className="text-white-50 mb-0 small">Détail des règlements</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedFacture && (
                        <>
                            <div className="summary-card-charte mb-4">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <strong>Facture {selectedFacture.numero_facture}</strong>
                                        <div className="small text-muted">Client: {selectedFacture.client}</div>
                                    </div>
                                    <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                                        <i className="ri-money-euro-circle-line"></i>
                                        {formatCurrency(selectedFacture.montant_ttc)}
                                    </span>
                                </div>
                            </div>
                            {selectedFacture.paiements?.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 table-charte">
                                        <thead>
                                            <tr>
                                                <th className="ps-4">Date</th>
                                                <th>N° Opération</th>
                                                <th>Mode</th>
                                                <th>Référence</th>
                                                <th className="text-end">Montant</th>
                                                <th className="text-center">Statut</th>
                                                <th className="pe-4">Saisi par</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedFacture.paiements.map((paiement, idx) => (
                                                <tr key={idx}>
                                                    <td className="ps-4 text-muted">{formatDate(paiement.date_reglement)}</td>
                                                    <td>
                                                        <span className="reference-badge-charte">
                                                            <i className="ri-hashtag"></i>{paiement.numero_reglement}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="mode-badge-charte">
                                                            <i className={getModePaiementIcon(paiement.mode_reglement)}></i>
                                                            {getModePaiementLabel(paiement.mode_reglement)}
                                                        </span>
                                                    </td>
                                                    <td className="text-muted">{paiement.reference || '—'}</td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.success }}>
                                                        {formatCurrency(paiement.montant)}
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="status-badge-charte" style={{
                                                            background: paiement.statut === 'valide'
                                                                ? `linear-gradient(135deg, ${COLORS.success}, #059669)`
                                                                : `linear-gradient(135deg, ${COLORS.warning}, #d97706)`
                                                        }}>
                                                            <i className={paiement.statut === 'valide' ? 'ri-check-line' : 'ri-time-line'}></i>
                                                            {paiement.statut}
                                                        </span>
                                                    </td>
                                                    <td className="pe-4">
                                                        <small>{paiement.prenom_utilisateur} {paiement.nom_utilisateur}</small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                       <tfoot>
    <tr>
        <td colSpan="4" className="text-end fw-bold ps-4">TOTAL PAYÉ :</td>
        <td className="text-end fw-bold" style={{ color: COLORS.success }}>
            {formatCurrency(
                selectedFacture.paiements.reduce(
                    (acc, p) => acc + (parseFloat(p.montant) || 0),
                    0
                )
            )}
        </td>
        <td colSpan="2"></td>
    </tr>
</tfoot>
                                    </Table>
                                </div>
                            ) : (
                                <div className="empty-state-charte">
                                    <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.info}15, ${COLORS.info}05)`, color: COLORS.info }}>
                                        <i className="ri-inbox-line"></i>
                                    </div>
                                    <h6 className="fw-semibold mb-1">Aucun paiement</h6>
                                    <p className="text-muted small mb-0">Aucun paiement enregistré pour cette facture</p>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowHistoriqueModal(false)} className="btn-modern-outline">Fermer</Button>
                </Modal.Footer>
            </Modal>

            {/* ===== MODAL RELANCE ===== */}
            <Modal show={showRelanceModal} onHide={() => setShowRelanceModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-mail-send-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Envoyer une relance</Modal.Title>
                            <p className="text-white-50 mb-0 small">Notifiez le client</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedFacture && (
                        <>
                            <div className="summary-card-charte mb-4">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <strong>{selectedFacture.numero_facture}</strong>
                                        <div className="small text-muted">{selectedFacture.client}</div>
                                    </div>
                                    <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                                        <i className="ri-money-euro-circle-line"></i>
                                        {formatCurrency(selectedFacture.reste_a_payer)}
                                    </span>
                                </div>
                            </div>
                            <Form.Group className="mb-4">
                                <Form.Label className="form-label-modern">Type de relance</Form.Label>
                                <InputGroup className="input-modern">
                                    <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                                    <Form.Select
                                        value={relanceData.type}
                                        onChange={(e) => setRelanceData({...relanceData, type: e.target.value})}
                                    >
                                        <option value="standard">Standard</option>
                                        <option value="premiere">Première relance</option>
                                        <option value="deuxieme">Deuxième relance - Urgent</option>
                                        <option value="derniere">Dernière relance - Mise en demeure</option>
                                    </Form.Select>
                                </InputGroup>
                            </Form.Group>
                            <Form.Group className="mb-0">
                                <Form.Label className="form-label-modern">Message personnalisé (optionnel)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={relanceData.message_personnalise}
                                    onChange={(e) => setRelanceData({...relanceData, message_personnalise: e.target.value})}
                                    placeholder="Ajoutez un message personnalisé si nécessaire..."
                                    className="textarea-modern"
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowRelanceModal(false)} className="btn-modern-outline">Annuler</Button>
                    <Button onClick={handleConfirmerRelance} disabled={sendingRelance} className="btn-modern-warning">
                        {sendingRelance ? (
                            <><Spinner size="sm" className="me-2" />Envoi...</>
                        ) : (
                            <><i className="ri-mail-send-line me-2"></i>Envoyer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--danger);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== HEADERS GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.7rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.row-late-charte { background: #fef2f2; }
                .table-charte tbody tr.row-late-charte:hover { background: #fee2e2; }
                .table-charte tfoot td {
                    background: #f8fafc;
                    padding: 0.85rem 0.75rem;
                    border-top: 2px solid #e2e8f0;
                    font-size: 0.85rem;
                }
                .table-link-charte {
                    color: var(--primary); text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                }
                .table-link-charte:hover {
                    color: var(--primary-dark);
                    text-decoration: underline;
                }

                /* ===== BADGES ===== */
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .mode-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; color: #475569;
                    padding: 0.25rem 0.65rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 500;
                }
                .rank-badge-charte {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 24px;
                    height: 24px;
                    border-radius: 6px;
                    background: ${COLORS.primary}15;
                    color: var(--primary);
                    font-weight: 700;
                    font-size: 0.7rem;
                }
                .badge-count-charte {
                    display: inline-flex;
                    align-items: center;
                    background: #f8fafc;
                    color: #64748b;
                    font-size: 0.6rem;
                    font-weight: 500;
                    padding: 0.15rem 0.5rem;
                    border-radius: 12px;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }
                .textarea-modern {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.8rem 0.9rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                    resize: vertical;
                }
                .textarea-modern:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary, .btn-modern-success, .btn-modern-warning {
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }
                .btn-modern-primary:disabled,
                .btn-modern-outline:disabled,
                .btn-modern-success:disabled,
                .btn-modern-warning:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--danger);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--danger);
                }

                /* ===== FORM CHECK ===== */
                .form-check-input:checked {
                    background-color: var(--primary);
                    border-color: var(--primary);
                }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .notification-charte {
                        min-width: auto !important;
                        width: calc(100% - 20px) !important;
                        max-width: none !important;
                        top: 10px !important;
                        right: 10px !important;
                        left: 10px !important;
                    }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}